import { defaultTo, everyTrue, someTrue } from 'aa_common/front-end/helpers'
import { isAccountingJournalTarget } from 'common/helpers'
import { FORM_MODES } from 'common/models'
import { useActionsStatus } from 'containers/assets/asset-list-container/hooks/use-actions-status'
import { get, isEqual } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectEventHistories } from 'store/asset/selectors'

import { useCorrection } from '../../hooks/use-correction'
import { useCorrectionBlocked } from './use-correction-blocked'
import { useIsCorrectable } from './use-is-correctable'
import { useIsFromSuspense } from './use-is-from-suspense'

type Params = {
  biid: string
  formMode: FORM_MODES
  asset: any
  isDraftFA?: boolean
}

// Everything AssetForm needs to know about correcting the fixed asset: the blocked reason, the field lock and which
// top messages to show.
export const useAssetCorrection = ({ biid, formMode, asset, isDraftFA }: Params) => {
  const isEditMode = formMode === FORM_MODES.EDIT
  const isCorrectionMode = formMode === FORM_MODES.CORRECTION
  const { findActionsStatusByBiid, getActionsStatus } = useActionsStatus()
  const isFromSuspense = useIsFromSuspense(asset)

  // The top messages and the blocked reason depend on isCorrectable, so they wait until isCheckingCorrectable settles.
  const { isCorrectable, isRetroActiveClosed, isCheckingCorrectable } = useIsCorrectable(
    asset,
    (isEditMode || isCorrectionMode) && !isDraftFA
  )

  useEffect(() => {
    getActionsStatus([biid])
  }, [])

  const actionsStatusByBiid = findActionsStatusByBiid(biid)
  const adjusted = get(actionsStatusByBiid, 'adjusted')

  const { change_situation, transition_at, acquired_at, usage_started_at, recognized_at } = defaultTo(asset, {})

  const {
    isCorrectable: isCorrectableWithPermission,
    isCorrectionLinkHidden,
    isCorrectionBlockedByEventHistory,
    checkEventsAllowedForCorrection,
    checkDateWithMonthlyClosing,
  } = useCorrection()
  const eventHistories = useSelector(selectEventHistories, isEqual)
  const isEventsAllowedForCorrection = checkEventsAllowedForCorrection(change_situation?.code)
  const isCorrectionBlockedByMonthlyClosing = !!transition_at && !checkDateWithMonthlyClosing(transition_at)
  // Gated by !isCorrectable below, so a retroactive FA the API allows correcting is never blocked by this.
  const isCorrectionBlockedByAcquisitionMonthlyClosing =
    !!acquired_at && !checkDateWithMonthlyClosing(acquired_at) && !transition_at
  // The registration-month reason only fits an FA registered (recognized_at) after its acquisition month. The API also
  // rejects a normal FA registered in its acquisition month with ERR_CORRECTION_MONTH_RETRO_ACTIVE_HAS_CLOSED once that
  // month is closed; there both months are the same, so the acquisition-month reason is shown instead.
  // The API's code has not been seen for an FA without usage_started_at, so that case also falls back to the dates:
  // registered in a closed month. With a usage start the dates alone cannot tell it apart from a normal FA whose
  // recognized_at moved past its acquisition month, so only the API decides.
  // An FA put to use in a later, still open month (its recognized_at moves there) is also rejected with that code,
  // but for its closed acquisition month: while the registration month is open, the acquisition-month reason wins.
  // A usage start registered in its own month moves recognized_at there, so recognized_at no longer marks the month
  // the FA was registered: a normal FA put to use in a later month gets the acquisition-month reason.
  // After a later event (除却 / 売却 ...) the current record carries that event's recognized_at, so the month the FA was
  // registered comes from its base record (取得 / 使用開始 / 移行受入) in the event history (AAISSUES-21046).
  const baseRecognizedAt =
    !!change_situation?.code && !isEventsAllowedForCorrection
      ? eventHistories.find(
          event => checkEventsAllowedForCorrection(event.change_situation?.code) && event.recognized_at
        )?.recognized_at
      : undefined
  const registeredAt = baseRecognizedAt || recognized_at
  const isRecognizedByUsageStart = !!usage_started_at && moment(usage_started_at).isSame(registeredAt, 'month')
  const isRegisteredAfterAcquisitionMonth =
    !!acquired_at && !!registeredAt && !isRecognizedByUsageStart && moment(acquired_at).isBefore(registeredAt, 'month')
  const isRegistrationMonthClosed = !!registeredAt && !checkDateWithMonthlyClosing(registeredAt)
  // An FA not put to use yet has no recognized_at, so the API's code alone shows the registration-month reason.
  // The base record's recognized_at is the registration date itself, so the dates alone decide there.
  const isBlockedByRetroactiveRegistration =
    (isRegisteredAfterAcquisitionMonth &&
      ((isRetroActiveClosed && (isRegistrationMonthClosed || !isCorrectionBlockedByAcquisitionMonthlyClosing)) ||
        (!transition_at && (!usage_started_at || !!baseRecognizedAt) && isRegistrationMonthClosed))) ||
    (isRetroActiveClosed && !transition_at && !usage_started_at && !recognized_at)

  // useCorrection folds the event-history side in (その他変更 included once the flag is on); the current change
  // situation is a separate signal and adds 区分変更 and 部分減損, which never reach the history list (AAISSUES-20763).
  const isCorrectionBlockedByEvents = someTrue(isCorrectionBlockedByEventHistory, !isEventsAllowedForCorrection)
  const { correctionBlockedReason, isCorrectionBlocked, isCorrectionBlockedReasonEnabled } = useCorrectionBlocked({
    biid,
    isCorrectionMode,
    isCorrectionBlockedByEventHistory: isCorrectionBlockedByEvents && !isCorrectable,
    isCorrectionBlockedByMonthlyClosing: isCorrectionBlockedByMonthlyClosing && !isCorrectable,
    transitionAt: transition_at,
    isCorrectionBlockedByAcquisitionMonthlyClosing: isCorrectionBlockedByAcquisitionMonthlyClosing && !isCorrectable,
    acquiredAt: acquired_at,
    isBlockedByRetroactiveRegistration,
    recognizedAt: registeredAt,
    adjusted: !!adjusted,
    isDraftFA,
  })
  const isShowTopMessageWithoutCorrect = someTrue(
    !isCorrectableWithPermission,
    !isEventsAllowedForCorrection,
    isCorrectionLinkHidden,
    everyTrue(isCorrectableWithPermission, !isEventsAllowedForCorrection)
  )
  // Accelerated depreciation only greys the link out on the legacy path; with the flag on the asset list keeps its
  // correction button clickable and the correction screen explains the reason instead.
  const isCorrectionLinkDisabled = everyTrue(!!adjusted, !isCorrectionBlockedReasonEnabled)

  const isPartialImpair = useMemo(() => {
    const accountingLedger = asset?.fixed_asset_ledgers?.find((ledger: any) =>
      isAccountingJournalTarget(ledger.ledger_setting)
    )
    return accountingLedger?.original_depreciation_method_code
  }, [asset])

  return {
    correctionBlockedReason: isCheckingCorrectable ? null : correctionBlockedReason,
    isCorrectionBlocked,
    isCorrectionLinkDisabled,
    isShowNoticeForChange: everyTrue(
      isEditMode,
      !isCheckingCorrectable,
      isShowTopMessageWithoutCorrect,
      !isCorrectable,
      !isDraftFA,
      !isCorrectionBlockedReasonEnabled
    ),
    isShowCorrectionNotice: everyTrue(isCorrectionMode, !isFromSuspense, !isDraftFA),
    isShowImportPartialMessage: everyTrue(
      isPartialImpair,
      isEditMode,
      !isCheckingCorrectable,
      !isCorrectable,
      !isShowTopMessageWithoutCorrect,
      !isDraftFA
    ),
    isShowCorrectionLink: everyTrue(
      isCorrectableWithPermission,
      isEditMode,
      !isCheckingCorrectable,
      !isDraftFA,
      someTrue(
        isCorrectionBlockedReasonEnabled,
        everyTrue(isCorrectable, !isCorrectionLinkHidden, !isShowTopMessageWithoutCorrect)
      )
    ),
    isShowSuspenseCorrectionBlock: everyTrue(isCorrectionMode, isFromSuspense, !isDraftFA),
  }
}
